import { superbarDarkTheme } from './dark.sb-theme';
import { superbarLightTheme } from './light.sb-theme';

export type ColorMode = 'light' | 'liquidGlass';

export type SuperbarTheme = {
  mode: ColorMode;
  className?: string;

  // Core tokens
  colors: {
    surface: {
      appBg: string; // outer shell background (panel)
      appBgMinimized: string; // closed/minimized shell background
      systemMessageBg: string; // system message header bg
      notificationBg: string; // mantine notification root
    };
    ink: {
      primary: string;
      secondary: string;
      tertiary: string;
      inverse: string;
      muted: string; // placeholder-ish / 50% text
      disabled: string; // loading text, etc
    };
    border: {
      shell: string; // container border
      systemMessageBottom: string;
    };
    selection: {
      bg: string;
      fg: string;
    };
  };

  shadows: {
    shell: string; // main panel shadow
    minimizedShell: string; // closed shell shadow
    systemMessageCloseButton: string;
    notification: string;
  };

  radii: {
    panel: string; // 28px
    minimizedPanelTop: string; // 28px
    minimizedPanel: string; // 20px
    pillButton: string; // 25px
    round: string; // 100%
  };

  // Component-level presets (so callers don't rebuild class strings)
  components: {
    chatShell: {
      borderColor: string;
      shadow: string;
      background: string; // for DEFAULT/MINIMIZED wrapper (not the motion root)
      className?: string;
    };
    chatShellClosed: {
      background: string;
      shadow: string;
      radius: string;
      className?: string;
    };

    systemMessage: {
      background: string;
      borderBottomColor: string;
      radiusTop: string;
      className?: string;
    };

    inputForm: {
      background: string;
      radiusTopWhenNoSystemMessage: string;
      radiusBottom: string;
      backdropFilter: string;
      boxShadow: string;
      className?: string;
    };

    menu: {
      iconColor: string;
      textColor: string;
      itemHoverBackground: string;
      className?: string;
      poweredBy: {
        iconBgColor: string;
        iconColor: string;
        dividerColor: string;
        className?: string;
      };
      closeButton: {
        background: string;
        borderColor: string;
        icon: string;
        iconHover: string;
        backgroundHover: string;
        className?: string;
      };
    };

    aiFilters: {
      titleColor: string;
      className?: string;
      nextQuestionButton: {
        background: string;
        borderColor: string;
        textColor: string;
        hoverBackground: string;
        className?: string;
      };
      filter: {
        background: string;
        textColor: string;
        borderColor: string;
        hoverBackground: string;
        clearIconColor: string;
        clearIconHoverColor: string;
        className?: string;
      };
    };

    loader: {
      color: string;
      opacity: string;
      stableOpacity: string;
      className?: string;
    };

    inputText: {
      text: string;
      textLoading: string;
      selectionBg: string;
      selectionText: string;
      className?: string;
      textShadow: string;
    };

    placeholder: {
      text: string;
      className?: string;
    };

    assistantMessage: {
      text: string;
      className?: string;
      ctaButton: {
        background: string;
        text: string;
        hoverBackground: string;
        className?: string;
      };
      closeButton: {
        background: string;
        borderColor: string;
        icon: string;
        iconHover: string;
        backgroundHover: string;
        className?: string;
      };
    };

    notification: {
      root: {
        background: string;
        border: string;
        radius: string;
        padding: string;
        shadow: string;
        className?: string;
      };
      title: {
        color: string;
        fontWeight: string;
        className?: string;
      };
      description: {
        color: string;
        className?: string;
      };
      closeButton: {
        color: string;
        hoverBg: string;
        hoverColor: string;
        className?: string;
      };
    };
  };
};

export { superbarDarkTheme, superbarLightTheme };

const themes: Record<ColorMode, SuperbarTheme> = {
  light: superbarLightTheme,
  liquidGlass: superbarDarkTheme,
};

export function getSuperbarTheme(mode: ColorMode): SuperbarTheme {
  return themes[mode] || superbarLightTheme;
}

// Coerces the persisted style value into a ColorMode. Also handles legacy
// 'classic' rows that may linger if the rename migration hasn't run yet.
export function styleToColorMode(style: unknown): ColorMode {
  return style === 'liquidGlass' ? 'liquidGlass' : 'light';
}
